/**
 * ScreeningSubTab - TASK-229/TASK-233/TASK-514
 * PEP, Sanctions, and Adverse-Media screening — REAL results from aml-service
 * (OpenSanctions sanctions+PEP + Event Registry adverse media, AI-triaged).
 *
 * Posture: every result is a *potential match requiring human review*, never a
 * confirmed determination. No fabricated vendors/HITs.
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  UserCheck,
  Shield,
  Newspaper,
  CheckCircle,
  AlertTriangle,
  Clock,
  CircleDashed,
  RefreshCw,
  History,
  Loader2,
} from 'lucide-react';
import {
  screenClient,
  type ScreeningResponse,
  type ClientScreeningResponse,
  decideMatch,
  getScreeningHistory,
  type ScreeningMatch,
  type MatchDecisionKind,
  type MatchDecision,
  type ScreeningHistory,
} from '../../../services/screeningService';
import { useComplianceRole } from '../../../hooks/useComplianceRole';
import { toast } from 'sonner';
import { usePersonalData } from '../../../hooks/useCompliance';

type ScreeningType = 'pep' | 'sanctions' | 'adverseMedia';
type ScreeningStatusType = 'clear' | 'hit' | 'pending_review' | 'not_screened';

interface ScreeningResult {
  type: ScreeningType;
  status: ScreeningStatusType;
  lastScreened?: string;
  hitCount: number;
  pendingReviewCount: number;
  provider?: string;
  details?: string;
  /** TASK-932: the raw sanctions/PEP matches, so the card can show and decide them.
   *  Adverse media is deliberately empty here — a news article has no stable entity id. */
  matches: ScreeningMatch[];
  auditId?: string | null;
  subjectRole: 'party' | 'beneficial_owner';
}

const screeningConfig = {
  pep: {
    title: 'PEP Status',
    icon: UserCheck,
    description: 'Politically Exposed Person screening',
    clearText: 'No PEP match found',
  },
  sanctions: {
    title: 'Sanctions',
    icon: Shield,
    description: 'Global sanctions lists (SECO, OFAC, EU, UN, UK)',
    clearText: 'No sanctions match found',
  },
  adverseMedia: {
    title: 'Adverse Media',
    icon: Newspaper,
    description: 'News-based negative-coverage scan',
    clearText: 'No adverse coverage found',
  },
};

const statusDisplay = {
  clear: {
    badge: 'bg-green-100 text-green-800',
    label: 'CLEAR',
    icon: CheckCircle,
    iconColor: 'text-green-600',
    bgColor: 'bg-green-50',
    borderColor: 'border-green-200',
  },
  hit: {
    badge: 'bg-red-100 text-red-800',
    label: 'HIT',
    icon: AlertTriangle,
    iconColor: 'text-red-600',
    bgColor: 'bg-red-50',
    borderColor: 'border-red-200',
  },
  pending_review: {
    badge: 'bg-amber-100 text-amber-800',
    label: 'POTENTIAL MATCH',
    icon: Clock,
    iconColor: 'text-amber-600',
    bgColor: 'bg-amber-50',
    borderColor: 'border-amber-200',
  },
  not_screened: {
    badge: 'bg-gray-100 text-gray-800',
    label: 'NOT SCREENED',
    icon: CircleDashed,
    iconColor: 'text-gray-600',
    bgColor: 'bg-gray-50',
    borderColor: 'border-gray-200',
  },
};

/** Map the aml-service screening response into the three UI cards.
 *  Backend statuses: "clear" | "potential_match" (+ "not_screened" for adverse media).
 *  hitCount stays 0 — we never render a "confirmed HIT"; matches surface as pending review. */
function mapToCards(resp: ScreeningResponse): Record<ScreeningType, ScreeningResult> {
  // Only an explicit "clear" reads green; not_screened AND any unrecognized status map to
  // not_screened (L1) — an un-run or unknown screen must never default to a green pass.
  const toStatus = (s: string): ScreeningStatusType =>
    s === 'potential_match' ? 'pending_review' : s === 'clear' ? 'clear' : 'not_screened';

  const sanctions = resp.categories.sanctions;
  const pep = resp.categories.pep;
  const am = resp.adverse_media;

  return {
    pep: {
      type: 'pep',
      matches: pep.matches,
      auditId: resp.audit_id ?? null,
      subjectRole: resp.subject_role ?? 'party',
      status: toStatus(pep.status),
      lastScreened: resp.screened_at,
      hitCount: 0,
      // TASK-932: a decided false positive is no longer PENDING — it is decided. It stays in
      // `matches` (still shown, marked) but stops counting as review to do.
      pendingReviewCount: pep.matches.filter((m) => m.decision?.decision !== 'false_positive').length,
      provider: resp.provider,
      details:
        pep.status === 'not_screened'
          ? 'PEP screening not configured'
          : pep.matches.length
          ? pep.matches.map((m) => m.caption).slice(0, 3).join('; ')
          : undefined,
    },
    sanctions: {
      type: 'sanctions',
      matches: sanctions.matches,
      auditId: resp.audit_id ?? null,
      subjectRole: resp.subject_role ?? 'party',
      status: toStatus(sanctions.status),
      lastScreened: resp.screened_at,
      hitCount: 0,
      // TASK-932: a decided false positive is no longer PENDING — it is decided. It stays in
      // `matches` (still shown, marked) but stops counting as review to do.
      pendingReviewCount: sanctions.matches.filter((m) => m.decision?.decision !== 'false_positive').length,
      provider: resp.provider,
      details:
        sanctions.status === 'not_screened'
          ? 'Sanctions screening not configured'
          : sanctions.matches.length
          ? sanctions.matches.map((m) => m.caption).slice(0, 3).join('; ')
          : undefined,
    },
    adverseMedia: {
      type: 'adverseMedia',
      matches: [],
      auditId: resp.audit_id ?? null,
      subjectRole: resp.subject_role ?? 'party',
      status: toStatus(am.status),
      lastScreened: resp.screened_at,
      hitCount: 0,
      pendingReviewCount: am.matches.length,
      provider: 'Event Registry (news)',
      details:
        am.status === 'not_screened'
          ? 'News screening not configured'
          : am.matches[0]?.title ?? undefined,
    },
  };
}

const DECISION_LABEL: Record<MatchDecisionKind, string> = {
  false_positive: 'False positive',
  confirmed: 'Confirmed',
  escalated: 'Escalated',
};

/**
 * TASK-932: one match, with what the firm decided about it and — for a CO or admin — the
 * control to decide. The match is never hidden by a decision: an auditor needs to see that it
 * was seen and what was concluded.
 */
const MatchRow: React.FC<{
  match: ScreeningMatch;
  clientId?: string;
  subjectRole: 'party' | 'beneficial_owner';
  auditId?: string | null;
  canDecide: boolean;
  onDecided: (decision: MatchDecision) => void;
}> = ({ match, clientId, subjectRole, auditId, canDecide, onDecided }) => {
  const [open, setOpen] = useState(false);
  const [decision, setDecision] = useState<MatchDecisionKind>('false_positive');
  const [rationale, setRationale] = useState('');
  const [saving, setSaving] = useState(false);
  const decided = match.decision;

  const submit = async () => {
    if (!clientId || !match.entity_id) return;
    if (!rationale.trim()) {
      toast.error('A decision needs a reason.');
      return;
    }
    setSaving(true);
    try {
      const out = await decideMatch(match.entity_id, {
        clientId, subjectRole, decision, rationale, auditId,
      });
      toast.success(
        `${DECISION_LABEL[decision]} recorded` +
          (out.radar_retracted ? ' — Radar subject retracted' : ''),
      );
      setOpen(false);
      setRationale('');
      onDecided(out.decision);
    } catch (e: any) {
      toast.error(e?.response?.status === 403
        ? 'Only a compliance officer or an admin can decide a match.'
        : 'The decision could not be recorded.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <li className="rounded-md border border-gray-200 bg-gray-50 p-3 text-left">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-gray-900">{match.caption}</span>
            <span className="text-xs text-gray-500">score {Math.round(match.score * 100)}%</span>
            {decided && (
              <span
                data-testid="match-decision"
                className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${
                  decided.decision === 'false_positive'
                    ? 'bg-gray-200 text-gray-700'
                    : decided.decision === 'confirmed'
                      ? 'bg-red-100 text-red-800'
                      : 'bg-amber-100 text-amber-800'
                }`}
                title={decided.rationale ?? undefined}
              >
                {DECISION_LABEL[decided.decision]}
              </span>
            )}
          </div>
          {match.url && (
            <a
              href={match.url}
              target="_blank"
              rel="noreferrer"
              className="text-xs text-blue-700 underline"
            >
              Source
            </a>
          )}
          {decided?.rationale && (
            <p className="mt-1 text-xs text-gray-600">“{decided.rationale}”</p>
          )}
        </div>
        {canDecide && match.entity_id && (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            className="shrink-0 text-xs text-kielis-navy underline"
          >
            {decided ? 'Re-decide' : 'Decide'}
          </button>
        )}
      </div>
      {open && (
        <div className="mt-3 space-y-2" data-testid="match-decide-form">
          <select
            value={decision}
            onChange={(e) => setDecision(e.target.value as MatchDecisionKind)}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          >
            <option value="false_positive">False positive — not our client</option>
            <option value="confirmed">Confirmed — it is our client</option>
            <option value="escalated">Escalate — needs further review</option>
          </select>
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            placeholder="Why — required. This is the record."
            rows={2}
            className="w-full rounded border border-gray-300 px-2 py-1 text-sm"
          />
          <button
            type="button"
            onClick={() => void submit()}
            disabled={saving || !rationale.trim()}
            className="rounded bg-kielis-navy px-3 py-1.5 text-sm font-medium text-white disabled:opacity-40"
          >
            {saving ? 'Recording…' : 'Record decision'}
          </button>
        </div>
      )}
    </li>
  );
};

const ScreeningStatusCard: React.FC<{
  type: ScreeningType;
  data: ScreeningResult;
  onRunScreening: () => void;
  clientId?: string;
  canDecide?: boolean;
  onDecided?: (decision: MatchDecision) => void;
}> = ({ type, data, onRunScreening, clientId, canDecide = false, onDecided }) => {
  const config = screeningConfig[type];
  const display = statusDisplay[data.status];
  const StatusIcon = display.icon;
  const TypeIcon = config.icon;

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className={`bg-white rounded-lg border-2 ${display.borderColor} p-5`}>
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${display.bgColor}`}>
            <TypeIcon className={`w-5 h-5 ${display.iconColor}`} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">{config.title}</h3>
            <p className="text-xs text-gray-500">{config.description}</p>
          </div>
        </div>
      </div>

      {/* Status Display */}
      <div className="text-center py-6">
        <StatusIcon className={`w-12 h-12 mx-auto mb-3 ${display.iconColor}`} />
        <span className={`inline-block px-4 py-1.5 rounded-full text-sm font-semibold ${display.badge}`}>
          {data.pendingReviewCount > 0
            ? `${data.pendingReviewCount} ${display.label}${data.pendingReviewCount > 1 ? 'ES' : ''}`
            : display.label}
        </span>

        {/* Context text */}
        <p className="text-sm text-gray-600 mt-3">
          {data.status === 'clear' ? config.clearText : data.details}
        </p>

        {/* Pending review badge */}
        {data.pendingReviewCount > 0 && (
          <p className="text-sm text-amber-600 mt-2">
            {data.pendingReviewCount} pending review
          </p>
        )}
      </div>

      {/* TASK-932: the matches themselves, with their decisions and the control to decide.
          The card used to render a count and a pill over a `ScreeningMatch` that already
          carried caption, score, verdict and URL — the drill-down was never built. */}
      {data.matches.length > 0 && (
        <ul className="mb-4 space-y-2" data-testid="screening-matches">
          {data.matches.map((m, i) => (
            <MatchRow
              key={m.entity_id ?? `${m.caption}-${i}`}
              match={m}
              clientId={clientId}
              subjectRole={data.subjectRole}
              auditId={data.auditId}
              canDecide={canDecide}
              onDecided={(d) => onDecided?.(d)}
            />
          ))}
        </ul>
      )}

      {/* Metadata */}
      <div className="text-xs text-gray-500 space-y-1 mb-4 pt-4 border-t border-gray-100">
        <div className="flex justify-between">
          <span>Last screened:</span>
          <span className="font-medium">{formatDate(data.lastScreened)}</span>
        </div>
        {data.provider && (
          <div className="flex justify-between">
            <span>Source:</span>
            <span className="font-medium">{data.provider}</span>
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="flex gap-2 pt-4 border-t border-gray-100">
        <button
          onClick={onRunScreening}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-2 text-sm text-kielis-navy hover:bg-kielis-gold/10 rounded"
        >
          <RefreshCw className="w-4 h-4" />
          Re-run
        </button>
      </div>
    </div>
  );
};

interface ScreeningSubTabProps {
  clientId?: string;
  clientName?: string;
}

export const ScreeningSubTab: React.FC<ScreeningSubTabProps> = ({ clientId, clientName }) => {
  const personal = usePersonalData();
  // TASK-932: deciding is a compliance control — the server gates on the JWT; this only
  // decides whether to SHOW the control.
  const { isCO, isAdmin } = useComplianceRole();
  const canDecide = isCO || isAdmin;
  const [history, setHistory] = useState<ScreeningHistory | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [data, setData] = useState<ClientScreeningResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Name from props, falling back to the KYC-loaded personal fields; dob/nationality
  // come from ComplianceContext (already loaded — no extra fetch).
  const name = (
    clientName ||
    [personal?.firstName?.value, personal?.lastName?.value].filter(Boolean).join(' ') ||
    ''
  ).trim();
  const birthDate = personal?.dateOfBirth?.value as string | undefined;
  const nationality = (personal?.nationalities?.value as string[] | undefined)?.[0];

  const runScreening = useCallback(async () => {
    if (!clientId) {
      setError('No client selected to screen.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      // Screens the party (identity from the KG, falling back to the props/context
      // name) + every beneficial owner. Each subject is audited server-side.
      const resp = await screenClient(clientId, { name, birthDate, nationality });
      setData(resp);
    } catch (e) {
      setError('Screening failed. Please try again.');
       
      console.error('[Screening] failed:', e);
    } finally {
      setLoading(false);
    }
  }, [clientId, name, birthDate, nationality]);

  // TASK-932 round 2: a decision used to call runScreening(), i.e. a full, audited, real
  // screen of the party and every owner on every "Record decision" click. The server
  // already annotates matches with their latest decision (`annotate_decisions`), keyed by
  // provider entity id — so apply the same annotation locally and leave the provider alone.
  const applyDecision = useCallback((d: MatchDecision) => {
    const stamp = (m: ScreeningMatch): ScreeningMatch =>
      m.entity_id === d.provider_entity_id
        ? { ...m, decision: { decision: d.decision, rationale: d.rationale,
                              decided_by: d.decided_by, decided_at: d.decided_at } }
        : m;
    const stampResp = (r: ScreeningResponse): ScreeningResponse => ({
      ...r,
      categories: {
        ...r.categories,
        sanctions: { ...r.categories.sanctions, matches: r.categories.sanctions.matches.map(stamp) },
        pep: { ...r.categories.pep, matches: r.categories.pep.matches.map(stamp) },
      },
    });
    setData((prev) => prev && {
      ...prev,
      party: prev.party ? stampResp(prev.party) : prev.party,
      beneficial_owners: prev.beneficial_owners.map((bo) => ({ ...bo, screening: stampResp(bo.screening) })),
    });
  }, []);

  // TASK-932: every run and every decision, newest first. `screening_repo.read_history`
  // existed with zero callers — this drawer is its first reader.
  const openHistory = useCallback(async () => {
    if (!clientId) return;
    setHistoryOpen(true);
    setHistoryLoading(true);
    try {
      setHistory(await getScreeningHistory(clientId));
    } catch {
      setHistory(null);
      toast.error('Screening history could not be loaded.');
    } finally {
      setHistoryLoading(false);
    }
  }, [clientId]);

  // Auto-fire once per client, and only once identity has loaded. `name` is empty
  // on mount and while usePersonalData() streams in, which would otherwise re-fire a
  // real, audited, billable screen on every partial-identity re-render. The backend
  // takes identity from the KG (props are only a fallback), so keying on clientId is
  // sufficient. Manual retry/run buttons call runScreening() directly, unaffected.
  const lastScreenedClientRef = useRef<string | null>(null);
  useEffect(() => {
    if (!clientId || !name) return;
    if (lastScreenedClientRef.current === clientId) return;
    lastScreenedClientRef.current = clientId;
    runScreening();
  }, [clientId, name, runScreening]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        <Loader2 className="w-6 h-6 animate-spin mr-3" />
        Screening {name || 'client'}…
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="text-center py-16">
        <AlertTriangle className="w-10 h-10 mx-auto mb-3 text-amber-500" />
        <p className="text-gray-700 mb-4">{error}</p>
        <button
          onClick={runScreening}
          className="px-4 py-2 bg-kielis-navy text-white rounded-lg text-sm font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data || !data.party) {
    return (
      <div className="text-center py-16 text-gray-500">
        <Shield className="w-10 h-10 mx-auto mb-3 text-gray-300" />
        No identity available to screen for this client.
      </div>
    );
  }

  const party = data.party;
  const screeningData = mapToCards(party);
  const beneficialOwners = data.beneficial_owners;
  const partyPending = Object.values(screeningData).reduce((s, c) => s + c.pendingReviewCount, 0);
  const boPending = beneficialOwners.filter((bo) => overallPending(bo.screening)).length;
  const hasPending = partyPending + boPending > 0;
  // A screen that did not run is NOT clear — it must never fold into the green banner.
  const anyNotScreened =
    overallNotScreened(party) || beneficialOwners.some((bo) => overallNotScreened(bo.screening));

  return (
    <div className="space-y-6">
      {/* Summary banner: potential matches > screening incomplete > all clear */}
      {hasPending ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-amber-600" />
          <div>
            <p className="font-medium text-amber-800">
              {partyPending + boPending} potential match{partyPending + boPending > 1 ? 'es' : ''} — review required
            </p>
            <p className="text-sm text-amber-700">
              Potential matches are candidates for human review, not confirmed determinations.
            </p>
          </div>
        </div>
      ) : anyNotScreened ? (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 flex items-center gap-3">
          <AlertTriangle className="w-6 h-6 text-gray-500" />
          <div>
            <p className="font-medium text-gray-800">Screening incomplete — some checks did not run</p>
            <p className="text-sm text-gray-600">
              This is not a clean result. Configure the screening provider and re-run before relying on it.
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-center gap-3">
          <CheckCircle className="w-6 h-6 text-green-600" />
          <div>
            <p className="font-medium text-green-800">All screening checks clear</p>
            <p className="text-sm text-green-700">
              No potential matches for the client{beneficialOwners.length ? ' or beneficial owners' : ''} across
              PEP, Sanctions, and Adverse Media
            </p>
          </div>
        </div>
      )}

      {/* Contracting party */}
      <div>
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-gray-700">
            Contracting party — {party.identity.name}
          </h4>
          <button
            type="button"
            onClick={() => void openHistory()}
            className="flex items-center gap-1 text-xs text-kielis-navy underline"
            data-testid="screening-history-open"
          >
            <History className="h-3.5 w-3.5" />
            History
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {(['pep', 'sanctions', 'adverseMedia'] as ScreeningType[]).map((type) => (
            <ScreeningStatusCard
              key={type}
              type={type}
              data={screeningData[type]}
              onRunScreening={runScreening}
              clientId={clientId}
              canDecide={canDecide}
              onDecided={applyDecision}
            />
          ))}
        </div>
      </div>

      {/* TASK-932: history drawer — every run and every decision for the client. */}
      {historyOpen && (
        <div
          className="fixed inset-y-0 right-0 z-40 w-full max-w-md overflow-y-auto border-l border-gray-200 bg-white p-5 shadow-xl"
          role="dialog"
          aria-label="Screening history"
          data-testid="screening-history"
        >
          <div className="mb-4 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-gray-900">Screening history</h4>
            <button type="button" onClick={() => setHistoryOpen(false)} className="text-xs text-gray-500 underline">
              Close
            </button>
          </div>
          {historyLoading && <p className="text-sm text-gray-500">Loading…</p>}
          {!historyLoading && history && (
            <>
              <h5 className="mb-2 text-xs font-semibold uppercase text-gray-500">Decisions</h5>
              {history.decisions.length === 0 ? (
                <p className="mb-4 text-sm text-gray-500">No decisions recorded yet.</p>
              ) : (
                <ul className="mb-4 space-y-2">
                  {history.decisions.map((d) => (
                    <li key={d.id} className="rounded border border-gray-200 p-2 text-sm">
                      <div className="flex justify-between">
                        <span className="font-medium">{DECISION_LABEL[d.decision]} · {d.provider_entity_id}</span>
                        <span className="text-xs text-gray-500">
                          {d.decided_at ? new Date(d.decided_at).toLocaleString() : ''}
                        </span>
                      </div>
                      {d.rationale && <p className="mt-1 text-xs text-gray-600">“{d.rationale}”</p>}
                      <p className="mt-1 text-[11px] text-gray-400">{d.subject_role} · by {d.decided_by}</p>
                    </li>
                  ))}
                </ul>
              )}
              <h5 className="mb-2 text-xs font-semibold uppercase text-gray-500">Runs</h5>
              {history.runs.length === 0 ? (
                <p className="text-sm text-gray-500">No screening runs recorded.</p>
              ) : (
                <ul className="space-y-1">
                  {history.runs.map((r) => (
                    <li key={r.id} className="flex justify-between text-xs">
                      <span>{r.subject_role} · {r.subject_name} · <span className="font-medium">{r.overall_status.replace('_', ' ')}</span></span>
                      <span className="text-gray-500">{new Date(r.screened_at).toLocaleString()}</span>
                    </li>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}

      {/* Beneficial owners */}
      {beneficialOwners.length > 0 && (
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-3">
            Beneficial owners ({beneficialOwners.length})
          </h4>
          <div className="space-y-2">
            {beneficialOwners.map((bo, i) => {
              const pending = overallPending(bo.screening);
              const notScreened = !pending && overallNotScreened(bo.screening);
              // TASK-932: an owner's matches are decidable the same way the party's are. The
              // pill alone left a BO's potential match with no drill-down and no control —
              // the backend accepted `subject_role='beneficial_owner'` from the start; the UI
              // never offered it.
              const boMatches = [
                ...bo.screening.categories.sanctions.matches,
                ...bo.screening.categories.pep.matches,
              ];
              return (
                <div
                  key={`${bo.subject.name}-${i}`}
                  className="bg-white border border-gray-200 rounded-lg px-4 py-3"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-800">{bo.subject.name}</span>
                    <span
                      className={`text-xs font-semibold px-3 py-1 rounded-full ${
                        pending
                          ? 'bg-amber-100 text-amber-800'
                          : notScreened
                          ? 'bg-gray-100 text-gray-800'
                          : 'bg-green-100 text-green-800'
                      }`}
                    >
                      {pending ? 'POTENTIAL MATCH' : notScreened ? 'NOT SCREENED' : 'CLEAR'}
                    </span>
                  </div>
                  {boMatches.length > 0 && (
                    <ul className="mt-3 space-y-2" data-testid={`bo-matches-${i}`}>
                      {boMatches.map((m, j) => (
                        <MatchRow
                          key={m.entity_id ?? `${m.caption}-${j}`}
                          match={m}
                          clientId={clientId}
                          subjectRole="beneficial_owner"
                          auditId={bo.screening.audit_id ?? null}
                          canDecide={canDecide}
                          onDecided={applyDecision}
                        />
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Honest disclaimer */}
      <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-500 flex items-start gap-2">
        <History className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <span>{party.disclaimer}</span>
      </div>
    </div>
  );
};

/** True if any category or adverse-media result is a potential match. */
function overallPending(resp: ScreeningResponse): boolean {
  return (
    resp.categories.sanctions.status === 'potential_match' ||
    resp.categories.pep.status === 'potential_match' ||
    resp.adverse_media.status === 'potential_match'
  );
}

/** True if any category or adverse-media screen did NOT produce a recognized result
 *  (not_screened OR any unrecognized status). Never rolls up to a green "clear" (L1). */
function overallNotScreened(resp: ScreeningResponse): boolean {
  const known = (s: string) => s === 'clear' || s === 'potential_match';
  return (
    !known(resp.categories.sanctions.status) ||
    !known(resp.categories.pep.status) ||
    !known(resp.adverse_media.status)
  );
}

export default ScreeningSubTab;
